import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { authManager } from '../utils/auth';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Cache for validation results to reduce network calls
const validationCache = {
    result: null,
    timestamp: null,
    CACHE_DURATION: 30 * 1000 // 30 seconds
};

/**
 * Validates session via backend with caching. authManager.authenticatedFetch sends the
 * httpOnly auth cookies (credentials: 'include') and on 401 attempts a cookie-based
 * refresh; if refresh fails it clears session and throws.
 * Backend must expose GET /users/validate returning 200 when token is valid, 401 otherwise.
 */
const validateSession = async () => {
  // isAuthenticated() is only a local UI flag - the real check is the backend
  // call below, since the actual token lives in an httpOnly cookie we can't read.
  if (!authManager.isAuthenticated()) return false;

  // Check cache first to reduce network calls
  const now = Date.now();
  if (validationCache.result !== null && 
      validationCache.timestamp && 
      (now - validationCache.timestamp) < validationCache.CACHE_DURATION) {
    if (process.env.NODE_ENV === 'development') {
      console.log('ProtectedRoute: Using cached validation result:', validationCache.result);
    }
    return validationCache.result;
  }
  
  try {
    // silent: true - a fresh page load with a stale isLoggedIn flag (e.g. cookies
    // expired since the last visit) shouldn't surface a "Session expired" toast;
    // it just silently redirects to login like any other unauthenticated visit.
    const response = await authManager.authenticatedFetch(
      `${BASE_URL}/users/validate`,
      { silent: true }
    );
    if (process.env.NODE_ENV === 'development') {
      console.log('ProtectedRoute: Backend validation response:', response.status);
      if (!response.ok) {
        const errorText = await response.text();
        console.log('ProtectedRoute: Validation error response:', errorText);
      }
    }
    
    const isValid = response.ok;
    
    // Cache the result
    validationCache.result = isValid;
    validationCache.timestamp = now;
    
    return isValid;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.log('ProtectedRoute: Validation error:', error.message);
      console.log('ProtectedRoute: Full error:', error);
    }
    
    // Cache negative result briefly to avoid repeated failed calls
    validationCache.result = false;
    validationCache.timestamp = now;
    
    return false;
  }
};

// Clear cache on token refresh or logout
const clearValidationCache = () => {
  validationCache.result = null;
  validationCache.timestamp = null;
};

// Listen for auth events to clear cache
window.addEventListener('auth:token-refreshed', clearValidationCache);
window.addEventListener('auth:logout', clearValidationCache);

const Loading = () => (
  <div className="d-flex justify-content-center align-items-center min-vh-100">
    <div className="text-center">
      <div className="spinner-border text-primary" role="status">
        <span className="sr-only">Loading...</span>
      </div>
      <p className="mt-2 mb-0">Verifying session...</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const [isValid, setIsValid] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const checkSession = async () => {
      const valid = await validateSession();
      if (!cancelled) {
        setIsValid(valid);
        if (!valid) {
          if (process.env.NODE_ENV === 'development') {
            console.log('ProtectedRoute: Validation failed, logging out');
          }
          authManager.logout();
        }
      }
    };

    checkSession();
    return () => {
      cancelled = true;
    };
  }, []);

  if (isValid === null) return <Loading />;
  if (!isValid) return <Navigate to="/" replace />;
  return children;
};

export default ProtectedRoute;
